import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  setSubstances,
  setLoading,
  setError,
  addItem,
  removeItem,
  updateItemField,
  selectSubstance,
  selectDosage,
  resetPrescription,
} from '../store/prescriptionSlice';
import {
  fetchSubstances,
  submitPrescription,
  updatePrescription,
  searchProduct,
} from '../services/prescription';
import productImage from '../assets/images/product.svg';
import badgeIcon from '../assets/icons/badge.svg';
import { stripWebInfoTitle } from '../utils/helpers';

export default function PrescriptionCard({
  summaryPrefill: summaryPrefillProp = null,
  reportContext: reportContextProp = null,
  bloodTestSummary: bloodTestSummaryProp = null,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { substances, items, loading } = useSelector(
    (state) => state.prescription
  );
  const userRole = useSelector((state) => state.user.role);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [searchingIndex, setSearchingIndex] = useState(null);
  const [productData, setProductData] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [selectedDaysOption, setSelectedDaysOption] = useState('');
  const [openSubstanceDropdown, setOpenSubstanceDropdown] = useState({});
  const [substanceSearchQuery, setSubstanceSearchQuery] = useState({});
  const [openDosageDropdown, setOpenDosageDropdown] = useState({});
  const [openFormDropdown, setOpenFormDropdown] = useState({});
  const [openMonthsDropdown, setOpenMonthsDropdown] = useState(false);
  const productRefs = useRef({});
  const substanceDropdownRefs = useRef({});
  const dosageDropdownRefs = useRef({});
  const formDropdownRefs = useRef({});
  const monthsDropdownRef = useRef(null);

  const editOrderDataRef = useRef(location.state?.editOrder || null);
  const editOrderData = editOrderDataRef.current;
  const summaryPrefill = summaryPrefillProp || location.state?.summaryPrefill || null;
  const reportContext = reportContextProp || location.state?.reportContext || {};
  const bloodTestSummary =
    bloodTestSummaryProp ?? location.state?.bloodTestSummary ?? null;
  const reportBloodTestId = String(
    reportContext?.bloodTestId ||
      reportContext?.blood_test_id ||
      location.state?.bloodTestId ||
      location.state?.blood_test_id ||
      ''
  ).trim();
  const reportRecordId = String(
    reportContext?.recordId || location.state?.recordId || ''
  ).trim();
  const isBloodTestLinked = Boolean(reportBloodTestId);
  const normalizedBloodTestId = Number(reportBloodTestId);
  const hasValidBloodTestId = Number.isInteger(normalizedBloodTestId);
  const hasPrefilledRef = useRef(false);
  const hasSummaryPrefilledRef = useRef(false);

  const displayBloodTestOrderId = (raw) =>
    String(raw || '')
      .trim()
      .replace(/^N:/, '');
  const formatBloodTestDate = (raw) =>
    String(raw || '')
      .trim()
      .replaceAll('/', '.');

  const normalizeTitle = (rawTitle) => {
    const key = String(rawTitle || '')
      .trim()
      .toLowerCase()
      .replace(/\./g, '');
    if (!key) return '';

    const map = {
      mr: 'Mr.',
      mrs: 'Mrs.',
      miss: 'Miss',
      ms: 'Ms.',
      dr: 'Dr.',
      doctor: 'Dr.',
      prof: 'Prof.',
      professor: 'Prof.',
      mx: 'Mx.',
      other: 'Other',
    };
    return map[key] || '';
  };

  useEffect(() => {
    if (location.state?.editOrder) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const [clientInfo, setClientInfo] = useState({
    title: '',
    firstName: '',
    lastName: '',
  });
  const [shippingInfo, setShippingInfo] = useState({
    addressLine1: '',
    streetNumber: '',
    postalCode: '',
    city: '',
    country: '',
    email: '',
  });

  useEffect(() => {
    if (!editOrderData) {
      setIsEditMode(false);
      setOrderId(null);
      hasPrefilledRef.current = false;
      hasSummaryPrefilledRef.current = false;

      setClientInfo({
        title: '',
        firstName: '',
        lastName: '',
      });
      setShippingInfo({
        addressLine1: '',
        streetNumber: '',
        postalCode: '',
        city: '',
        country: '',
        email: '',
      });
      setSelectedDaysOption('');

      dispatch(resetPrescription());
    }
  }, [editOrderData, dispatch]);

  useEffect(() => {
    if (
      editOrderData ||
      hasSummaryPrefilledRef.current ||
      !summaryPrefill ||
      typeof summaryPrefill !== 'object'
    ) {
      return;
    }

    hasSummaryPrefilledRef.current = true;
    const safe = (v) => (typeof v === 'string' ? v.trim() : '');
    const client = summaryPrefill.clientInfo || {};
    const shipping = summaryPrefill.shippingInfo || {};

    setClientInfo((prev) => ({
      ...prev,
      title: normalizeTitle(safe(client.title)),
      firstName: safe(client.firstName),
      lastName: safe(client.lastName),
    }));

    setShippingInfo((prev) => ({
      ...prev,
      addressLine1: safe(shipping.addressLine1),
      streetNumber: safe(shipping.streetNumber),
      postalCode: safe(shipping.postalCode),
      city: safe(shipping.city),
      country: safe(shipping.country),
      email: safe(shipping.email),
    }));
  }, [editOrderData, summaryPrefill]);

  useEffect(() => {
    let cancelled = false;
    const loadSubstances = async () => {
      try {
        dispatch(setLoading(true));
        const data = await fetchSubstances();
        if (!cancelled) {
          dispatch(setSubstances(data));
        }
      } catch (error) {
        if (!cancelled) {
          dispatch(setError(error.message || 'Failed to load substances'));
        }
      }
    };
    loadSubstances();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      editOrderData &&
      substances.length > 0 &&
      !loading &&
      !hasPrefilledRef.current
    ) {
      hasPrefilledRef.current = true;
      setIsEditMode(true);
      setOrderId(editOrderData.orderId || null);

      if (editOrderData.clientInfo) {
        setClientInfo({
          title: editOrderData.clientInfo.title || '',
          firstName: editOrderData.clientInfo.firstName || '',
          lastName: editOrderData.clientInfo.lastName || '',
        });
      }

      if (editOrderData.shippingInfo) {
        setShippingInfo({
          addressLine1:
            editOrderData.shippingInfo.address1 ||
            editOrderData.shippingInfo.addressLine1 ||
            '',
          streetNumber:
            editOrderData.shippingInfo.streetNumber ||
            '',
          postalCode: editOrderData.shippingInfo.postalCode || '',
          city: editOrderData.shippingInfo.city || '',
          country: editOrderData.shippingInfo.country || '',
          email: editOrderData.shippingInfo.email || '',
        });
      }

      if (
        editOrderData.prescriptions &&
        editOrderData.prescriptions.length > 0
      ) {
        dispatch(resetPrescription());

        const prefillPrescriptions = async () => {
          for (
            let index = 0;
            index < editOrderData.prescriptions.length;
            index++
          ) {
            const prescription = editOrderData.prescriptions[index];

            if (index > 0) {
              dispatch(addItem());
            }

            await new Promise((resolve) => setTimeout(resolve, 50));

            if (prescription.substance) {
              dispatch(
                selectSubstance({
                  index,
                  substanceName: prescription.substance,
                })
              );

              await new Promise((resolve) => setTimeout(resolve, 100));

              if (prescription.dosage) {
                const substance = substances.find(
                  (s) => s.substance_name === prescription.substance
                );
                if (substance && substance.available_dosage) {
                  let dosageToMatch = prescription.dosage;
                  if (
                    prescription.dosageType &&
                    !prescription.dosage.includes(prescription.dosageType)
                  ) {
                    dosageToMatch =
                      `${prescription.dosage} ${prescription.dosageType}`.trim();
                  }

                  const matchingDosage = substance.available_dosage.find(
                    (d) => {
                      const dLower = d.toLowerCase().trim();
                      const matchLower = dosageToMatch.toLowerCase().trim();
                      return (
                        dLower === matchLower ||
                        dLower.includes(matchLower) ||
                        matchLower.includes(dLower) ||
                        dLower.replace(/\s+/g, ' ') ===
                          matchLower.replace(/\s+/g, ' ')
                      );
                    }
                  );

                  if (matchingDosage) {
                    dispatch(
                      selectDosage({ index, dosageString: matchingDosage })
                    );
                  } else if (substance.available_dosage.length > 0) {
                    dispatch(
                      selectDosage({
                        index,
                        dosageString: substance.available_dosage[0],
                      })
                    );
                  }
                }
              }

              if (prescription.frequency) {
                dispatch(
                  updateItemField({
                    index,
                    field: 'frequency',
                    value: prescription.frequency,
                  })
                );
              }
              if (prescription.days) {
                dispatch(
                  updateItemField({
                    index,
                    field: 'days',
                    value: prescription.days,
                  })
                );
              }
              if (prescription.form) {
                const validForms = ['Capsule', 'Tablet', 'Liquid', 'Powder'];
                const formValue = validForms.includes(prescription.form)
                  ? prescription.form
                  : 'Tablet';
                dispatch(
                  updateItemField({ index, field: 'form', value: formValue })
                );
              }
            }
          }
        };

        prefillPrescriptions();
      }
    }
  }, [editOrderData, substances, loading, dispatch]);

  const handleAddItem = async () => {
    if (items.length > 0) {
      const lastItemIndex = items.length - 1;
      const lastItem = items[lastItemIndex];
      
      if (!validatePrescriptionItem(lastItem, true)) {
        return;
      }

      const searchSuccess = await handleSearchProduct(lastItemIndex);
      if (!searchSuccess) {
        return;
      }
      
      const newItemIndex = items.length;
      dispatch(addItem());
      
      if (selectedDaysOption) {
        const daysMap = {
          '1 Month': 30,
          '2 Months': 60,
          '3 Months': 90,
          '4 Months': 120,
          '5 Months': 150,
          '6 Months': 180,
        };
        const daysValue = daysMap[selectedDaysOption];
        if (daysValue) {
          dispatch(updateItemField({ index: newItemIndex, field: 'days', value: daysValue.toString() }));
        }
      }
    } else {
      const newItemIndex = items.length;
      dispatch(addItem());
      
      if (selectedDaysOption) {
        const daysMap = {
          '1 Month': 30,
          '2 Months': 60,
          '3 Months': 90,
          '4 Months': 120,
          '5 Months': 150,
          '6 Months': 180,
        };
        const daysValue = daysMap[selectedDaysOption];
        if (daysValue) {
          dispatch(updateItemField({ index: newItemIndex, field: 'days', value: daysValue.toString() }));
        }
      }
    }
  };

  const handleRemoveItem = (index) => {
    dispatch(removeItem(index));

    setProductData((prev) => {
      const newData = { ...prev };
      delete newData[index];

      const updatedData = {};
      Object.keys(newData).forEach((key) => {
        const keyNum = parseInt(key, 10);
        if (keyNum > index) {
          updatedData[keyNum - 1] = newData[key];
        } else if (keyNum < index) {
          updatedData[keyNum] = newData[key];
        }
      });
      return updatedData;
    });
    setExpandedItems((prev) => {
      const newExpanded = { ...prev };
      delete newExpanded[index];

      const updatedExpanded = {};
      Object.keys(newExpanded).forEach((key) => {
        const keyNum = parseInt(key, 10);
        if (keyNum > index) {
          updatedExpanded[keyNum - 1] = newExpanded[key];
        } else if (keyNum < index) {
          updatedExpanded[keyNum] = newExpanded[key];
        }
      });
      return updatedExpanded;
    });
  };

  const handleSubstanceChange = (index, substanceName) => {
    dispatch(selectSubstance({ index, substanceName }));
    setOpenSubstanceDropdown((prev) => ({ ...prev, [index]: false }));
    setSubstanceSearchQuery((prev) => ({ ...prev, [index]: '' }));
  };

  const toggleSubstanceDropdown = (index) => {
    setOpenSubstanceDropdown((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
    if (!openSubstanceDropdown[index]) {
      setSubstanceSearchQuery((prev) => ({ ...prev, [index]: '' }));
    }
  };

  const handleSubstanceSearch = (index, query) => {
    setSubstanceSearchQuery((prev) => ({ ...prev, [index]: query }));
  };

  const getFilteredSubstances = (index) => {
    const query = (substanceSearchQuery[index] || '').toLowerCase().trim();
    if (!query) return substances;
    return substances.filter((sub) =>
      sub.substance_name.toLowerCase().includes(query)
    );
  };

  const handleDosageChange = (index, dosageString) => {
    dispatch(selectDosage({ index, dosageString }));
    setOpenDosageDropdown((prev) => ({ ...prev, [index]: false }));
  };

  const toggleDosageDropdown = (index) => {
    setOpenDosageDropdown((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleFormChange = (index, formValue) => {
    dispatch(updateItemField({ index, field: 'form', value: formValue }));
    setOpenFormDropdown((prev) => ({ ...prev, [index]: false }));
  };

  const toggleFormDropdown = (index) => {
    setOpenFormDropdown((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleMonthsDropdown = () => {
    setOpenMonthsDropdown((prev) => !prev);
  };

  const handleMonthsChange = (value) => {
    handleDaysOptionChange(value);
    setOpenMonthsDropdown(false);
  };

  const handleFieldChange = (index, field, value) => {
    dispatch(updateItemField({ index, field, value }));
  };

  const handleDaysOptionChange = (value) => {
    setSelectedDaysOption(value);
    
    if (value) {
      const daysMap = {
        '1 Month': 30,
        '2 Months': 60,
        '3 Months': 90,
        '4 Months': 120,
        '5 Months': 150,
        '6 Months': 180,
      };
      
      const daysValue = daysMap[value];
      
      items.forEach((_, index) => {
        dispatch(updateItemField({ index, field: 'days', value: daysValue.toString() }));
      });
    }
  };

  const toggleProductDetails = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const validatePrescriptionItem = (item, showToast = true) => {
    if (!item.substance) {
      if (showToast) toast.error('Please select a substance first.');
      return false;
    }
    if (!item.selectedDosage) {
      if (showToast) toast.error('Please select a dosage first.');
      return false;
    }
    if (!item.form) {
      if (showToast) toast.error('Please select a form first.');
      return false;
    }
    if (!item.frequency) {
      if (showToast) toast.error('Please enter a daily frequency.');
      return false;
    }
    const frequency = parseInt(item.frequency, 10);
    if (item.form === 'Liquid') {
      if (frequency <= 0 || frequency > 40) {
        if (showToast)
          toast.error('Please enter a valid daily frequency for liquid form (1-40).');
        return false;
      }
    } else {
      if (frequency <= 0 || frequency > 9) {
        if (showToast)
          toast.error('Please enter a valid daily frequency (1-9).');
        return false;
      }
    }
    if (!item.days) {
      if (showToast) toast.error('Please enter a number of days.');
      return false;
    }
    const days = parseInt(item.days, 10);
    if (days <= 0 || days > 352) {
      if (showToast)
        toast.error('Please enter a valid number of days (1-352).');
      return false;
    }
    return true;
  };

  const handleSearchProduct = async (index) => {
    const item = items[index];
    
    if (!validatePrescriptionItem(item, true)) {
      return;
    }

    try {
      setSearchingIndex(index);

      const productData = {
        substance: item.substance,
        dosage: item.selectedDosage,
        dosageType: item.dosageType,
        form: item.form,
        frequency: item.frequency ? parseInt(item.frequency, 10) : item.frequency,
        days: item.days ? parseInt(item.days, 10) : item.days,
      };

      const response = await searchProduct(productData);

      if (response.error) {
        toast.error(response.error);
        return false;
      } else if (response.message && !response.success) {
        toast.warning(response.message);
        return false;
      } else if (response.success && response.data) {
        // Store product data for this item index
        setProductData((prev) => ({
          ...prev,
          [index]: response.data,
        }));
        // Automatically expand the accordion when product is found
        setExpandedItems((prev) => ({
          ...prev,
          [index]: true,
        }));
        toast.success('Product found successfully!');
        return true;
      } else {
        toast.error('Unexpected response from server.');
        return false;
      }
    } catch (error) {
      const errorMessage =
        error.message || 'Failed to search for product. Please try again.';
      toast.error(errorMessage);
      return false;
    } finally {
      setSearchingIndex(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.target;
    const firstInvalid = form.querySelector(':invalid');
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.reportValidity();
      return;
    }

    try {
      setSubmitting(true);

      const selectedSubstances = items.filter((item) => item.substance);
      if (selectedSubstances.length === 0) {
        toast.error('Please select at least one substance.');
        setSubmitting(false);
        return;
      }

      const prescriptionData = {
        client: clientInfo,
        shipping: shippingInfo,
        prescriptions: selectedSubstances.map((item) => ({
          substance: item.substance,
          dosage: item.selectedDosage,
          dosageType: item.dosageType,
          form: item.form,
          frequency: item.frequency
            ? parseInt(item.frequency, 10)
            : item.frequency,
          days: item.days ? parseInt(item.days, 10) : item.days,
        })),
      };

      let responseData;
      if (isEditMode && orderId) {
        prescriptionData.order_id = orderId;
        responseData = await updatePrescription(prescriptionData);
      } else {
        // Send blood_test_id only when created from blood test context.
        if (isBloodTestLinked && hasValidBloodTestId) {
          prescriptionData.blood_test_id = normalizedBloodTestId;
        }
        responseData = await submitPrescription(prescriptionData);
      }

      if (responseData?.success === false) {
        let errorMessage =
          isEditMode
            ? 'Failed to update prescription. Please try again.'
            : 'Failed to submit prescription. Please try again.';

        if (responseData.errors?.error) {
          errorMessage = responseData.errors.error;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        }

        toast.error(errorMessage);
        setSubmitting(false);
        return;
      }

      // Success cases - navigate to order detail page
      const orderIdFromResponse = responseData?.order_id;

      if (isEditMode) {
        if (responseData?.warning) {
          toast.warning(responseData.warning);
        } else {
          toast.success('Prescription updated successfully!');
        }
      } else {
        if (responseData?.warning) {
          toast.warning(responseData.warning);
        } else {
          toast.success('Prescription submitted successfully!');
        }
      }

      if (!isEditMode && isBloodTestLinked) {
        setTimeout(() => {
          if (orderIdFromResponse) {
            navigate(`/order-details/${orderIdFromResponse}`);
            return;
          }
          if (userRole === 'Supervisor') {
            navigate('/order-history');
            return;
          }

          navigate('/your-advice', {
            state: {
              reportContext: {
                recordId: reportRecordId || null,
                bloodTestId: reportBloodTestId || null,
              },
            },
          });
        }, 3000);
      } else if (orderIdFromResponse) {
        setTimeout(() => {
          navigate(`/order-details/${orderIdFromResponse}`);
        }, 3000);
      } else {
        // Only reset form if not in edit mode and no order_id
        if (!isEditMode) {
          dispatch(resetPrescription());
          setClientInfo({
            title: '',
            firstName: '',
            lastName: '',
          });
          setShippingInfo({
            addressLine1: '',
            streetNumber: '',
            postalCode: '',
            city: '',
            country: '',
            email: '',
          });
          setSelectedDaysOption('');
        }
      }
    } catch (error) {
      let errorMessage =
        isEditMode
          ? 'Failed to update prescription. Please try again.'
          : 'Failed to submit prescription. Please try again.';

      if (error.response?.data) {
        const responseData = error.response.data;
        if (responseData.errors?.error) {
          errorMessage = responseData.errors.error;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle Substance dropdowns
      Object.keys(openSubstanceDropdown).forEach((index) => {
        if (
          openSubstanceDropdown[index] &&
          substanceDropdownRefs.current[index] &&
          !substanceDropdownRefs.current[index].contains(event.target)
        ) {
          setOpenSubstanceDropdown((prev) => ({ ...prev, [index]: false }));
          setSubstanceSearchQuery((prev) => ({ ...prev, [index]: '' }));
        }
      });

      // Handle Dosage dropdowns
      Object.keys(openDosageDropdown).forEach((index) => {
        if (
          openDosageDropdown[index] &&
          dosageDropdownRefs.current[index] &&
          !dosageDropdownRefs.current[index].contains(event.target)
        ) {
          setOpenDosageDropdown((prev) => ({ ...prev, [index]: false }));
        }
      });

      // Handle Form dropdowns
      Object.keys(openFormDropdown).forEach((index) => {
        if (
          openFormDropdown[index] &&
          formDropdownRefs.current[index] &&
          !formDropdownRefs.current[index].contains(event.target)
        ) {
          setOpenFormDropdown((prev) => ({ ...prev, [index]: false }));
        }
      });

      // Handle Months dropdown
      if (
        openMonthsDropdown &&
        monthsDropdownRef.current &&
        !monthsDropdownRef.current.contains(event.target)
      ) {
        setOpenMonthsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openSubstanceDropdown, openDosageDropdown, openFormDropdown, openMonthsDropdown]);

  return (
    <div className="w-full max-w-6xl mx-auto">
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-2xl p-8 md:p-10 pb-12 md:pb-14 shadow-sm font-poppins">
          <div className="space-y-8">
            {userRole === 'Supervisor' && isBloodTestLinked ? (
              <section
                className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFA] px-4 py-4 md:px-6 md:py-5"
                aria-label="Blood test details"
              >
                <h2 className="text-[#2B3133] font-poppins font-medium text-[18px] md:text-[20px] mb-3 md:mb-4">
                  Blood test
                </h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm md:text-base font-poppins">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <dt className="text-[#889696] shrink-0">Order ID:</dt>
                    <dd className="text-[#1E2426] font-medium break-all">
                      {displayBloodTestOrderId(bloodTestSummary?.orderId) || ''}
                    </dd>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <dt className="text-[#889696] shrink-0">Type:</dt>
                    <dd className="text-[#1E2426] font-medium">
                      {String(bloodTestSummary?.testType || '').trim() || ''}
                    </dd>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <dt className="text-[#889696] shrink-0">Test date:</dt>
                    <dd className="text-[#1E2426] font-medium">
                      {formatBloodTestDate(bloodTestSummary?.testDate) || ''}
                    </dd>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <dt className="text-[#889696] shrink-0">Result date:</dt>
                    <dd className="text-[#1E2426] font-medium">
                      {formatBloodTestDate(bloodTestSummary?.resultDate) || ''}
                    </dd>
                  </div>
                </dl>
              </section>
            ) : null}
            <section>
              <h2 className="text-[#2B3133] font-poppins font-medium text-[20px] mb-4">
                Client Info
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    Title
                  </label>
                  <div className="relative h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
                    <select
                      value={clientInfo.title}
                      onChange={(e) =>
                        setClientInfo({ ...clientInfo, title: e.target.value })
                      }
                      className="w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer"
                      required
                    >
                      <option value="">Select Title</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Miss">Miss</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Prof.">Prof.</option>
                      <option value="Mx.">Mx.</option>
                      <option value="Other">Other</option>
                    </select>
                    <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                          fill="#2D4343"
                          stroke="#2D4343"
                          strokeWidth="0.5"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    First Name
                  </label>
                  <input
                    value={clientInfo.firstName}
                    onChange={(e) =>
                      setClientInfo({
                        ...clientInfo,
                        firstName: e.target.value,
                      })
                    }
                    className="h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                    placeholder="Enter your client first name"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    Last Name
                  </label>
                  <input
                    value={clientInfo.lastName}
                    onChange={(e) =>
                      setClientInfo({ ...clientInfo, lastName: e.target.value })
                    }
                    className="h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                    placeholder="Enter your client last name"
                    required
                  />
                </div>
              </div>
              <div className="h-px bg-[#E6E6E6] my-6" />
            </section>

            <section>
              <h2 className="text-[#2B3133] font-poppins font-medium text-[20px] mb-4">
                Shipment Address
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    Street Name
                  </label>
                  <input
                    value={shippingInfo.addressLine1}
                    onChange={(e) =>
                      setShippingInfo({
                        ...shippingInfo,
                        addressLine1: e.target.value,
                      })
                    }
                    className="h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                    placeholder="What's your shipping address?"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    Street Number
                  </label>
                  <input
                    value={shippingInfo.streetNumber}
                    onChange={(e) =>
                      setShippingInfo({
                        ...shippingInfo,
                        streetNumber: e.target.value,
                      })
                    }
                    className="h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                    placeholder="Enter your street number"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    Postal code
                  </label>
                  <input
                    value={shippingInfo.postalCode}
                    onChange={(e) =>
                      setShippingInfo({
                        ...shippingInfo,
                        postalCode: e.target.value,
                      })
                    }
                    className="h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                    placeholder="Your postal code"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    City
                  </label>
                  <input
                    value={shippingInfo.city}
                    onChange={(e) =>
                      setShippingInfo({ ...shippingInfo, city: e.target.value })
                    }
                    className="h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                    placeholder="Which city are you in?"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-sm">
                    Country
                  </label>
                  <input
                    value={shippingInfo.country}
                    onChange={(e) =>
                      setShippingInfo({
                        ...shippingInfo,
                        country: e.target.value,
                      })
                    }
                    className="h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                    placeholder="Choose your country"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-4 w-full md:w-[540px]">
                <label className="text-[#889696] font-poppins text-sm">
                  Shipping e-mail address
                </label>
                <input
                  type="email"
                  value={shippingInfo.email}
                  onChange={(e) =>
                    setShippingInfo({ ...shippingInfo, email: e.target.value })
                  }
                  className="h-[52px] w-full rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none"
                  placeholder="Your email for shipping updates"
                  required
                />
              </div>
              <div className="h-px bg-[#E6E6E6] my-6" />
            </section>

            <section>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4 md:gap-0">
                <h2 className="text-[#2B3133] font-poppins font-medium text-[20px]">
                  Prescription
                </h2>
                <div className="flex flex-col gap-2">
                  <label className="text-[#889696] font-poppins text-xs">
                    Select Months
                  </label>
                  <div
                    ref={monthsDropdownRef}
                    className="relative h-[52px] rounded-xl border border-[#CBD5E0] bg-white w-full md:w-[180px]"
                  >
                    <button
                      type="button"
                      onClick={toggleMonthsDropdown}
                      className="w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer text-left"
                    >
                      <span
                        className={`${
                          selectedDaysOption
                            ? 'text-[#2B3133]'
                            : 'text-gray-400'
                        }`}
                      >
                        {selectedDaysOption || 'Select Months'}
                      </span>
                    </button>
                    <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`transition-transform ${
                          openMonthsDropdown ? 'rotate-180' : ''
                        }`}
                      >
                        <path
                          d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                          fill="#2D4343"
                          stroke="#2D4343"
                          strokeWidth="0.5"
                        />
                      </svg>
                    </div>
                    {openMonthsDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-[#CBD5E0] rounded-xl shadow-lg max-h-[300px] overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => handleMonthsChange('')}
                          className={`w-full text-left px-4 py-2.5 hover:bg-[#F8F8F8] transition-colors text-sm ${
                            !selectedDaysOption
                              ? 'bg-[#E7F6EF] text-[#119975] font-medium'
                              : 'text-[#2B3133]'
                          }`}
                        >
                          Select Months
                        </button>
                        {[
                          '1 Month',
                          '2 Months',
                          '3 Months',
                          '4 Months',
                          '5 Months',
                          '6 Months',
                        ].map((monthOption) => (
                          <button
                            key={monthOption}
                            type="button"
                            onClick={() => handleMonthsChange(monthOption)}
                            className={`w-full text-left px-4 py-2.5 hover:bg-[#F8F8F8] transition-colors text-sm ${
                              selectedDaysOption === monthOption
                                ? 'bg-[#E7F6EF] text-[#119975] font-medium'
                                : 'text-[#2B3133]'
                            }`}
                          >
                            {monthOption}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-[#C1DCD4] border-t-[#119975] rounded-full" />
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((it, idx) => (
                    <div
                      key={idx}
                      className="relative bg-[#FBF6F2] border border-[#F1EAE4] rounded-2xl px-6 pt-6 pb-8 md:pr-16 font-poppins font-normal"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-[minmax(200px,1.2fr)_minmax(170px,0.7fr)_minmax(130px,0.5fr)_minmax(130px,0.5fr)_100px_100px] gap-4 items-end">
                        <div className="flex flex-col gap-2">
                          <label className="text-[#889696] font-poppins text-xs">
                            Substance
                          </label>
                          <div
                            ref={(el) => (substanceDropdownRefs.current[idx] = el)}
                            className="relative h-[52px] rounded-xl border border-[#CBD5E0] bg-white"
                          >
                            <button
                              type="button"
                              onClick={() => toggleSubstanceDropdown(idx)}
                              className="w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer text-left overflow-hidden"
                            >
                              <span
                                className={`whitespace-nowrap overflow-hidden text-ellipsis block ${
                                  it.substance ? 'text-[#2B3133]' : 'text-gray-400'
                                }`}
                              >
                                {it.substance || 'Select Substance'}
                              </span>
                            </button>
                            <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                              <svg
                                width="14"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={`transition-transform ${
                                  openSubstanceDropdown[idx] ? 'rotate-180' : ''
                                }`}
                              >
                                <path
                                  d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                                  fill="#2D4343"
                                  stroke="#2D4343"
                                  strokeWidth="0.5"
                                />
                              </svg>
                            </div>
                            {openSubstanceDropdown[idx] && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-[#CBD5E0] rounded-xl shadow-lg max-h-[300px] overflow-hidden">
                                <div className="p-2 border-b border-[#EDEEF0]">
                                  <input
                                    type="text"
                                    placeholder="Search substance..."
                                    value={substanceSearchQuery[idx] || ''}
                                    onChange={(e) =>
                                      handleSubstanceSearch(idx, e.target.value)
                                    }
                                    className="w-full h-[40px] px-3 rounded-lg border border-[#CBD5E0] bg-[#F8F8F8] outline-none focus:border-[#119975] focus:bg-white text-sm"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                <div className="max-h-[240px] overflow-y-auto">
                                  {getFilteredSubstances(idx).length > 0 ? (
                                    getFilteredSubstances(idx).map((sub) => (
                                      <button
                                        key={sub.substance_name}
                                        type="button"
                                        onClick={() =>
                                          handleSubstanceChange(
                                            idx,
                                            sub.substance_name
                                          )
                                        }
                                        className={`w-full text-left px-4 py-2.5 hover:bg-[#F8F8F8] transition-colors text-sm ${
                                          it.substance === sub.substance_name
                                            ? 'bg-[#E7F6EF] text-[#119975] font-medium'
                                            : 'text-[#2B3133]'
                                        }`}
                                      >
                                        {sub.substance_name}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                      No substances found
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-[#889696] font-poppins text-xs">
                            Dosage
                          </label>
                          <div
                            ref={(el) => (dosageDropdownRefs.current[idx] = el)}
                            className="relative h-[52px] rounded-xl border border-[#CBD5E0] bg-white"
                          >
                            <button
                              type="button"
                              onClick={() => toggleDosageDropdown(idx)}
                              disabled={
                                !it.substance ||
                                it.availableDosages.length === 0
                              }
                              className="w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer text-left disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                            >
                              <span
                                className={`whitespace-nowrap overflow-hidden text-ellipsis block ${
                                  it.selectedDosage
                                    ? 'text-[#2B3133]'
                                    : 'text-gray-400'
                                }`}
                              >
                                {it.selectedDosage || 'Select Dosage'}
                              </span>
                            </button>
                            <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                              <svg
                                width="14"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={`transition-transform ${
                                  openDosageDropdown[idx] ? 'rotate-180' : ''
                                }`}
                              >
                                <path
                                  d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                                  fill="#2D4343"
                                  stroke="#2D4343"
                                  strokeWidth="0.5"
                                />
                              </svg>
                            </div>
                            {openDosageDropdown[idx] && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-[#CBD5E0] rounded-xl shadow-lg max-h-[300px] overflow-y-auto">
                                <button
                                  type="button"
                                  onClick={() => handleDosageChange(idx, '')}
                                  className={`w-full text-left px-4 py-2.5 hover:bg-[#F8F8F8] transition-colors text-sm ${
                                    !it.selectedDosage
                                      ? 'bg-[#E7F6EF] text-[#119975] font-medium'
                                      : 'text-[#2B3133]'
                                  }`}
                                >
                                  Select Dosage
                                </button>
                                {it.availableDosages.map((dosage, dIdx) => (
                                  <button
                                    key={dIdx}
                                    type="button"
                                    onClick={() =>
                                      handleDosageChange(idx, dosage)
                                    }
                                    className={`w-full text-left px-4 py-2.5 hover:bg-[#F8F8F8] transition-colors text-sm ${
                                      it.selectedDosage === dosage
                                        ? 'bg-[#E7F6EF] text-[#119975] font-medium'
                                        : 'text-[#2B3133]'
                                    }`}
                                  >
                                    {dosage}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-[#889696] font-poppins text-xs">
                            Dosage Type
                          </label>
                          <input
                            value={it.dosageType}
                            readOnly
                            placeholder="Auto-filled"
                            className="h-[52px] rounded-xl border border-[#CBD5E0] bg-gray-50 px-4 outline-none text-gray-600"
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-[#889696] font-poppins text-xs">
                            Form
                          </label>
                          <div
                            ref={(el) => (formDropdownRefs.current[idx] = el)}
                            className="relative h-[52px] rounded-xl border border-[#CBD5E0] bg-white"
                          >
                            <button
                              type="button"
                              onClick={() => toggleFormDropdown(idx)}
                              className="w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer text-left"
                            >
                              <span className="text-[#2B3133]">
                                {it.form || 'Tablet'}
                              </span>
                            </button>
                            <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                              <svg
                                width="14"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={`transition-transform ${
                                  openFormDropdown[idx] ? 'rotate-180' : ''
                                }`}
                              >
                                <path
                                  d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
                                  fill="#2D4343"
                                  stroke="#2D4343"
                                  strokeWidth="0.5"
                                />
                              </svg>
                            </div>
                            {openFormDropdown[idx] && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-[#CBD5E0] rounded-xl shadow-lg max-h-[300px] overflow-y-auto">
                                {['Capsule', 'Tablet', 'Liquid', 'Powder'].map(
                                  (formOption) => (
                                    <button
                                      key={formOption}
                                      type="button"
                                      onClick={() =>
                                        handleFormChange(idx, formOption)
                                      }
                                      className={`w-full text-left px-4 py-2.5 hover:bg-[#F8F8F8] transition-colors text-sm ${
                                        (it.form || 'Tablet') === formOption
                                          ? 'bg-[#E7F6EF] text-[#119975] font-medium'
                                          : 'text-[#2B3133]'
                                      }`}
                                    >
                                      {formOption}
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-[#889696] font-poppins text-xs whitespace-nowrap">
                            Daily Frequency
                          </label>
                          <input
                            type="number"
                            value={it.frequency}
                            onChange={(e) =>
                              handleFieldChange(
                                idx,
                                'frequency',
                                e.target.value
                              )
                            }
                            min="1"
                            max={it.form === 'Liquid' ? '40' : '9'}
                            required
                            placeholder="e.g., 2"
                            className="h-[52px] w-full rounded-xl border border-[#CBD5E0] bg-white px-4 outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-[#889696] font-poppins text-xs whitespace-nowrap">
                            Number of Days
                          </label>
                          <input
                            type="number"
                            value={it.days}
                            onChange={(e) =>
                              handleFieldChange(idx, 'days', e.target.value)
                            }
                            min="1"
                            max="352"
                            required
                            placeholder="Days"
                            className="h-[52px] w-full rounded-xl border border-[#CBD5E0] bg-white px-4 outline-none"
                          />
                        </div>
                      </div>
                      <div className="absolute right-4 top-4 flex flex-row md:flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => handleSearchProduct(idx)}
                          disabled={searchingIndex === idx}
                          className="w-[20px] h-[20px] md:w-[26px] md:h-[26px] rounded-full grid place-items-center bg-[#119975] hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed relative group"
                          aria-label="Search Product"
                          title="Search Product"
                        >
                          {searchingIndex === idx ? (
                            <div className="animate-spin h-3 w-3 md:h-4 md:w-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <svg
                              className="w-3 h-3 md:w-4 md:h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Search Product
                          </span>
                        </button>
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(idx)}
                            className="w-[20px] h-[20px] md:w-[26px] md:h-[26px] rounded-full grid place-items-center relative group"
                            aria-label="Remove"
                            title="Remove"
                          >
                            <span
                              className="relative block w-[20px] h-[20px] md:w-[26px] md:h-[26px] rounded-full"
                              style={{ backgroundColor: '#F75E5A' }}
                            >
                              <svg
                                className="absolute inset-0 m-auto"
                                width="9"
                                height="9"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3 3l6 6M9 3L3 9"
                                  stroke="#FFFFFF"
                                  strokeWidth="1.75"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </span>
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              Remove
                            </span>
                          </button>
                        )}
                        {productData[idx] && (
                          <button
                            type="button"
                            onClick={() => toggleProductDetails(idx)}
                            className="w-[20px] h-[20px] md:w-[26px] md:h-[26px] rounded-full grid place-items-center bg-gray-300 hover:bg-gray-400 transition relative group"
                            aria-label={expandedItems[idx] ? 'Collapse' : 'Expand'}
                            title={expandedItems[idx] ? 'Collapse' : 'Expand'}
                          >
                            <svg
                              className={`w-3 h-3 md:w-4 md:h-4 transition-transform ${
                                expandedItems[idx] ? 'rotate-180' : ''
                              }`}
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6 9L12 15L18 9"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {expandedItems[idx] ? 'Collapse' : 'Expand'}
                            </span>
                          </button>
                        )}
                      </div>
                      {expandedItems[idx] && productData[idx] && (
                        <div className="mt-6 pt-6 border-t border-[#EDEEF0]">
                          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                            <div className="hidden md:block">
                              <div className="bg-[#E8E8E8] px-8 py-6 grid grid-cols-[3fr_1fr_1fr_1fr] gap-4 items-center font-poppins text-xs font-medium text-[#2B3133] uppercase">
                                <div>SUPPLEMENT</div>
                                <div className="text-left">PRICE</div>
                                <div className="text-center">QUANTITY</div>
                                <div className="text-right">FINAL PRICE</div>
                              </div>
                              <div className="px-8 py-6 grid grid-cols-[3fr_1fr_1fr_1fr] gap-4 items-center">
                                <div className="flex items-center gap-3 font-poppins pr-4 border-r border-[#EDEEF0] relative">
                                  <div className="w-14 h-14 bg-[#F5F5F5] rounded-lg flex items-center justify-center shrink-0">
                                    <img
                                      src={
                                        productData[idx].image &&
                                        productData[idx].image.trim() !== ''
                                          ? productData[idx].image
                                          : productImage
                                      }
                                      alt={productData[idx].name || 'Product'}
                                      className="w-full h-full object-contain p-2"
                                    />
                                  </div>
                                  <div
                                    className="flex-1 relative"
                                    ref={(el) => (productRefs.current[idx] = el)}
                                    onMouseEnter={(e) => {
                                      if (productData[idx]?.web_info) {
                                        const h3Element =
                                          e.currentTarget.querySelector('h3');
                                        const rect = h3Element
                                          ? h3Element.getBoundingClientRect()
                                          : e.currentTarget.getBoundingClientRect();
                                        setTooltipPosition({
                                          top: rect.bottom + 16,
                                          left: rect.left,
                                        });
                                        setHoveredProductId(idx);
                                      }
                                    }}
                                    onMouseLeave={() => setHoveredProductId(null)}
                                  >
                                    <h3 className="font-semibold text-[#2B3133] text-sm mb-0.5 cursor-pointer hover:text-[#119975] transition-colors flex items-center gap-1">
                                      {productData[idx].brand &&
                                        productData[idx].brand.trim() !== '' && (
                                          <>
                                            {productData[idx].brand}{' '}
                                          </>
                                        )}
                                      {productData[idx].name || 'N/A'}
                                      {productData[idx].is_complex && (
                                        <img
                                          src={badgeIcon}
                                          alt="Complex"
                                          className="w-5 h-5"
                                        />
                                      )}
                                    </h3>
                                    <p className="text-gray-500 text-xs">
                                      {parseInt(productData[idx].content, 10) || 'N/A'}{' '}
                                      {productData[idx].content_unit || ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-[#2B3133] text-sm text-left font-poppins">
                                  {productData[idx].price
                                    ? `€${parseFloat(
                                        productData[idx].price
                                      ).toFixed(2)}`
                                    : '€0.00'}
                                </div>
                                <div className="text-[#2B3133] text-sm text-center font-poppins">
                                  {productData[idx].required_quantity || 'N/A'}
                                </div>
                                <div className="text-[#119975] text-sm font-semibold text-right font-poppins">
                                  {productData[idx].final_price
                                    ? `€${parseFloat(
                                        productData[idx].final_price
                                      ).toFixed(2)}`
                                    : '€0.00'}
                                </div>
                              </div>
                            </div>
                            <div className="md:hidden p-4">
                              <div className="bg-[#FBF6F2] rounded-2xl p-4 font-poppins">
                                <div className="flex items-start gap-3 mb-4">
                                  <div className="w-14 h-14 bg-[#F5F5F5] rounded-lg flex items-center justify-center shrink-0">
                                    <img
                                      src={
                                        productData[idx].image &&
                                        productData[idx].image.trim() !== ''
                                          ? productData[idx].image
                                          : productImage
                                      }
                                      alt={productData[idx].name || 'Product'}
                                      className="w-full h-full object-contain p-2"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-[#2B3133] text-sm mb-1 flex items-center gap-1">
                                      {productData[idx].brand &&
                                        productData[idx].brand.trim() !== '' && (
                                          <>
                                            {productData[idx].brand}{' '}
                                          </>
                                        )}
                                      {productData[idx].name || 'N/A'}
                                      {productData[idx].is_complex && (
                                        <img
                                          src={badgeIcon}
                                          alt="Complex"
                                          className="w-5 h-5"
                                        />
                                      )}
                                    </h3>
                                    <p className="text-gray-500 text-xs">
                                      {parseInt(productData[idx].content, 10) || 'N/A'}{' '}
                                      {productData[idx].content_unit || ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                                    <span className="text-[#2B3133] text-sm font-medium">
                                      Price
                                    </span>
                                    <span className="text-[#2B3133] text-sm">
                                      {productData[idx].price
                                        ? `€${parseFloat(
                                            productData[idx].price
                                          ).toFixed(2)}`
                                        : '€0.00'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                                    <span className="text-[#2B3133] text-sm font-medium">
                                      Quantity
                                    </span>
                                    <span className="text-[#2B3133] text-sm">
                                      {productData[idx].required_quantity || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between py-2">
                                    <span className="text-[#2B3133] text-sm font-medium">
                                      Final Price
                                    </span>
                                    <span className="text-[#119975] text-base font-semibold">
                                      {productData[idx].final_price
                                        ? `€${parseFloat(
                                            productData[idx].final_price
                                          ).toFixed(2)}`
                                        : '€0.00'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="inline-flex items-center gap-2 md:gap-4 bg-[#119975] text-[#FFEBDB] pl-2 md:pl-3 pr-4 md:pr-6 h-[40px] md:h-[60px] rounded-full font-poppins text-sm md:text-base"
                  >
                    <span className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 grid place-items-center text-[20px] md:text-[24px] leading-none">
                      +
                    </span>
                    Add more
                  </button>
                </div>
              )}
            </section>
          </div>

          <div className="-mt-[40px] md:-mt-[60px] flex justify-end">
            <button
              type="submit"
              disabled={submitting || loading}
              className="inline-flex items-center justify-center h-[40px] md:h-[60px] px-6 md:px-10 rounded-full bg-[#119975] text-[#FFEBDB] font-poppins text-base md:text-lg font-medium shadow-sm hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Submitting...'
                : isEditMode
                  ? 'Update'
                  : 'Submit'}
            </button>
          </div>
        </div>
      </form>

      {hoveredProductId !== null &&
        productData[hoveredProductId]?.web_info &&
        createPortal(
          <div
            className="fixed z-[9999] w-[500px] max-w-[90vw] bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-h-[70vh] overflow-y-auto"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
            onMouseEnter={() => setHoveredProductId(hoveredProductId)}
            onMouseLeave={() => setHoveredProductId(null)}
          >
            <div
              className="text-sm text-[#2B3133] font-poppins tooltip-content"
              dangerouslySetInnerHTML={{
                __html: stripWebInfoTitle(
                  productData[hoveredProductId]?.web_info || ''
                ),
              }}
              style={{
                fontSize: '13px',
                lineHeight: '1.6',
              }}
            />
            <style>{`
            .tooltip-content table {
              width: 100%;
              border-collapse: collapse;
              margin: 8px 0;
            }
            .tooltip-content table td,
            .tooltip-content table th {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
            }
            .tooltip-content table strong {
              font-weight: 600;
            }
            .tooltip-content b {
              font-weight: 600;
            }
            .tooltip-content p {
              margin: 8px 0;
            }
          `}</style>
          </div>,
          document.body
        )}
    </div>
  );
}
